/**
 * ConfirmChoice — small portaled modal that asks the user to pick
 * between 2-3 named actions (or cancel). Used wherever we need a
 * forced-choice between non-trivial options (e.g. "Replace matrix"
 * vs "Merge into current matrix").
 *
 * Differs from a yes/no confirm() in that the action labels are
 * verbose ("Replace" / "Merge") — meaning the user reads what they're
 * about to do, not just OK/Cancel. Esc dismisses. Cancel button
 * always present; click backdrop dismisses too.
 */

import { For, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';

export interface ConfirmChoiceOption {
  /** Unique key returned via onChoose. */
  key: string;
  /** Button label (verbose, action-shaped). */
  label: string;
  /** Optional one-line clarification under the label. */
  hint?: string;
  /** Visual tone — controls accent color. */
  tone?: 'primary' | 'danger' | 'neutral';
}

interface ConfirmChoiceProps {
  title: string;
  message: string;
  options: ConfirmChoiceOption[];
  onChoose: (key: string) => void;
  onCancel: () => void;
}

export function ConfirmChoice(props: ConfirmChoiceProps) {
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') props.onCancel();
  }
  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  return (
    <Portal mount={shellPortal() ?? document.body}>
      <div
        class="confirm-overlay"
        onClick={props.onCancel}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
      >
        <div class="confirm-modal confirm-choice" onClick={(e) => e.stopPropagation()}>
          <div class="confirm-title">{props.title}</div>
          <div class="confirm-message">{props.message}</div>
          <div class="confirm-choice-actions">
            <For each={props.options}>
              {(opt) => (
                <button
                  type="button"
                  class="confirm-choice-button"
                  data-tone={opt.tone ?? 'primary'}
                  onClick={() => props.onChoose(opt.key)}
                >
                  <span class="confirm-choice-label">{opt.label}</span>
                  {opt.hint ? <span class="confirm-choice-hint">{opt.hint}</span> : null}
                </button>
              )}
            </For>
            <button
              type="button"
              class="confirm-choice-button"
              data-tone="cancel"
              onClick={props.onCancel}
            >
              <span class="confirm-choice-label">Cancel</span>
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
